import { api } from '@orisini/ui/lib/api'
import type { DataTemplate } from '@orisini/studio-kit/templates/data-template'
import type { Values } from '@orisini/studio-kit/templates/types'

/** Klien produksi konten: Workflow, Review, Content Bank, editor, Create. */

export type ProductionStatus = 'drafting' | 'draft' | 'approved' | 'producing' | 'ready' | 'failed' | 'rejected'
export type PublishStatus = 'none' | 'awaiting_approval' | 'queued' | 'scheduled' | 'published' | 'failed'
export type Destination = 'bank' | 'queue' | 'publish'
export type Kind = 'poster' | 'carousel' | 'video'
export type DurationMode = 'auto' | '1-5' | '5-10' | 'custom'
export interface DurationSpec {
  mode: DurationMode
  minutes?: number | null
}

/** Sama dengan AGENT_THRESHOLD_SEC di studio-kit: di atas ini wajib render di Orisini Agent. */
export const AGENT_THRESHOLD_SEC = 600

export const DURATION_LABEL: Record<DurationMode, string> = {
  auto: 'Auto',
  '1-5': '1–5 mins',
  '5-10': '5–10 mins',
  custom: 'Custom',
}

export interface ContentItem {
  id: string
  workspaceId: string
  workspaceName: string
  /** NULL = project tanpa akun: hanya bisa diunduh. */
  platform: string | null
  productCode: 'pinboss' | 'gramium' | null
  kind: Kind
  style: string | null
  origin: 'discover' | 'brief' | 'csv'
  title: string
  sourceUrl: string | null
  caption: string | null
  productionStatus: ProductionStatus
  publishStatus: PublishStatus
  destination: Destination
  thumbUrl: string | null
  fileCount: number
  /** Video: durasi hasil render atau rencana dari langkah suara. */
  durationSec: number | null
  renderLocation: 'container' | 'agent' | null
  error: string | null
  scheduledAt: number | null
  publishedUrl: string | null
  createdAt: number
  updatedAt: number
}

export interface Copy {
  values: Values
  /** Video: adegan, masing-masing dengan `narration`. */
  slides?: Values[]
  caption: string
  hashtags: string[]
  voiceover?: { assetId: string; scenes: { start: number; end: number }[] } | null
  language?: string | null
}

export interface Asset {
  id: string
  kind: 'image' | 'audio' | 'video'
  mime: string
  sizeBytes: number
  durationMs: number | null
  filename: string | null
  url: string
}

export interface VideoDetail {
  duration: DurationSpec | null
  voiceover: Asset | null
  audio: { role: 'backsound' | 'sfx'; label: string; asset: Asset | null }[]
  broll: Asset[]
  voice: ({ scene: number; durationMs: number; url: string } | null)[]
  totalSec: number | null
  location: 'container' | 'agent' | null
  job: { id: string; location: 'container' | 'agent'; status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled'; progress: number | null; error: string | null; totalSec: number; credits: number; createdAt: number; updatedAt: number } | null
  agentThresholdSec: number
}

export interface ContentDetail {
  content: ContentItem
  source: { title: string; url: string | null; text: string; siteName: string | null; partial: boolean; notes?: string | null }
  copy: Copy | null
  template: { def: DataTemplate; ref: string; version: number }
  files: { id: string; position: number; ratio: string; width: number; height: number; role: 'image' | 'video' | 'thumb'; mime: string; durationMs: number | null; url: string }[]
  video?: VideoDetail
  history: HistoryEvent[]
  workflow: { allowEdit: boolean; allowRegenerate: boolean }
}

export interface HistoryEvent {
  actor: 'user' | 'system'
  type: string
  detail: Record<string, unknown> | null
  at: number
  contentId?: string
  title?: string
  kind?: Kind
}

export interface Workflow {
  mode: 'approval' | 'auto'
  allowEdit: boolean
  allowRegenerate: boolean
  requirePublishApproval: boolean
  destination: Destination
}

export interface BriefInput {
  urls: string[]
  title: string
  text: string
  notes: string
  imageAssetId?: string | null
  imageUrl?: string | null
  workspaceIds: string[]
  kinds: Kind[]
  style?: string | null
  destination?: Destination | null
  /** Video saja. */
  duration?: DurationSpec | null
  voiceoverAssetId?: string | null
  audio?: { assetId: string; role: 'backsound' | 'sfx'; label: string }[]
  brollAssetIds?: string[]
}

export interface AudioItem {
  id: string
  role: 'backsound' | 'sfx'
  label: string
  asset: Asset
}

export type ContentAction = 'approve' | 'reject' | 'regenerate' | 'render' | 'queue' | 'publish' | 'keep'

export const contentApi = {
  workflow: (wsId: string) => api.get<{ workflow: Workflow; platform: string }>(`/studio/workspaces/${wsId}/workflow`),
  saveWorkflow: (wsId: string, wf: Workflow) => api.put<{ workflow: Workflow }>(`/studio/workspaces/${wsId}/workflow`, wf).then((r) => r.workflow),
  history: (wsId: string) => api.get<{ events: HistoryEvent[] }>(`/studio/workspaces/${wsId}/history`).then((r) => r.events),

  produceFromDiscover: (ids: string[], kinds: Kind[]) => api.post<{ created: number }>(`/studio/discover/produce`, { ids, kinds }),

  review: (tab: 'drafts' | 'publish', workspaceId?: string) =>
    api.get<{ items: ContentItem[]; reviewCounts: { drafts: number; publish: number } }>(
      `/studio/contents?view=review&tab=${tab}${workspaceId ? `&workspaceId=${encodeURIComponent(workspaceId)}` : ''}`,
    ),
  bank: (status: string, workspaceId?: string) =>
    api.get<{ items: ContentItem[]; reviewCounts: { drafts: number; publish: number } }>(
      `/studio/contents?view=bank&status=${status}${workspaceId ? `&workspaceId=${encodeURIComponent(workspaceId)}` : ''}`,
    ),
  reviewCount: () => api.get<{ drafts: number; publish: number }>(`/studio/review/count`),
  get: (id: string) => api.get<ContentDetail>(`/studio/contents/${id}`),
  save: (id: string, copy: Copy) => api.put(`/studio/contents/${id}`, { copy }),
  act: (id: string, action: ContentAction) => api.post(`/studio/contents/${id}/${action}`, {}),
  bulk: (ids: string[], action: 'approve' | 'reject') => api.post<{ updated: number }>(`/studio/contents-bulk`, { ids, action }),
  remove: (id: string) => api.delete(`/studio/contents/${id}`),

  brief: (b: BriefInput) => api.post<{ created: number }>(`/studio/briefs`, b),
  importRows: (filename: string, mapping: unknown, rows: Record<string, unknown>[]) =>
    api.post<{ created: number; accepted: number; rejected: { row: number; reason: string }[] }>(`/studio/briefs/import`, { filename, mapping, rows }),
  presets: () => api.get<{ presets: { id: string; name: string; mapping: unknown }[] }>(`/studio/mapping-presets`).then((r) => r.presets),
  savePreset: (name: string, mapping: unknown) => api.post(`/studio/mapping-presets`, { name, mapping }),
  deletePreset: (id: string) => api.delete(`/studio/mapping-presets/${id}`),

  audioLibrary: (wsId: string) => api.get<{ items: AudioItem[] }>(`/studio/workspaces/${wsId}/audio`).then((r) => r.items),
  addAudio: (wsId: string, item: { assetId: string; role: 'backsound' | 'sfx'; label: string }) => api.post(`/studio/workspaces/${wsId}/audio`, item),
  removeAudio: (wsId: string, itemId: string) => api.delete(`/studio/workspaces/${wsId}/audio/${itemId}`),
}

/**
 * Unggah audio/video/foto ke Studio per potongan 10 MB — berkas besar tidak
 * muat dalam satu request ke Worker. `onProgress` menerima 0..1.
 */
export async function uploadAsset(file: File, onProgress?: (p: number) => void): Promise<Asset> {
  const mime = file.type || (file.name.toLowerCase().endsWith('.mp3') ? 'audio/mpeg' : file.name.toLowerCase().endsWith('.wav') ? 'audio/wav' : '')
  const u = await api.post<{ id: string; partSize: number; parts: number }>(`/studio/uploads`, { filename: file.name, mime, size: file.size })
  try {
    for (let n = 1; n <= u.parts; n++) {
      const part = file.slice((n - 1) * u.partSize, Math.min(file.size, n * u.partSize))
      const res = await fetch(`/api/studio/uploads/${u.id}/parts/${n}`, { method: 'PUT', body: part, credentials: 'include', headers: { 'content-type': 'application/octet-stream' } })
      if (!res.ok) {
        const b = (await res.json().catch(() => ({}))) as { error?: { message?: string } }
        throw new Error(b.error?.message ?? `Upload failed (${res.status})`)
      }
      onProgress?.(n / u.parts)
    }
    return (await api.post<{ asset: Asset }>(`/studio/uploads/${u.id}/complete`, {})).asset
  } catch (err) {
    void api.delete(`/studio/uploads/${u.id}`).catch(() => undefined)
    throw err
  }
}

export function formatDuration(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return ''
  const s = Math.round(sec)
  const m = Math.floor(s / 60)
  const h = Math.floor(m / 60)
  return h ? `${h}:${String(m % 60).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}` : `${m}:${String(s % 60).padStart(2, '0')}`
}

export const PRODUCTION_LABEL: Record<ProductionStatus, string> = {
  drafting: 'Writing draft',
  draft: 'Draft',
  approved: 'Approved',
  producing: 'Producing',
  ready: 'Ready',
  failed: 'Failed',
  rejected: 'Rejected',
}

export const PUBLISH_LABEL: Record<PublishStatus, string> = {
  none: 'In the Bank',
  awaiting_approval: 'Waiting for approval',
  queued: 'In the queue',
  scheduled: 'Scheduled',
  published: 'Published',
  failed: 'Publishing failed',
}

export const DESTINATION_LABEL: Record<Destination, string> = {
  bank: 'Save to Content Bank',
  queue: 'Add to Publishing Queue',
  publish: 'Publish immediately',
}

const EVENT_LABEL: Record<string, string> = {
  created: 'Created',
  drafted: 'Draft written',
  regenerate: 'Asked to rewrite the draft',
  edited: 'Edited',
  approved: 'Approved',
  rejected: 'Rejected',
  render: 'Asked to render again',
  produced: 'Produced',
  failed: 'Failed',
  awaiting_publish_approval: 'Waiting for approval to publish',
  kept_in_bank: 'Kept in the Content Bank',
  queued: 'Added to the queue',
  sent_to_post: 'Sent to post now',
  publish_failed: 'Publishing failed',
  publish_queued: 'Queued on the platform',
  publish_scheduled: 'Scheduled on the platform',
  publish_published: 'Published',
  publish_failed_platform: 'Failed on the platform',
  publish_none: 'Removed from the queue',
  render_queued: 'Sent to the video renderer',
  waiting_for_agent: 'Waiting for Orisini Agent to render',
  script_adjusted: 'Script length adjusted to fit the duration',
}

/** Kalimat riwayat yang bisa dibaca: label + rincian penting (AI, style, template, error). */
export function describeEvent(e: HistoryEvent): string {
  const d = e.detail ?? {}
  const parts = [EVENT_LABEL[e.type] ?? e.type]
  if (e.type === 'drafted') parts.push(d.usedAi ? 'by AI' : 'from the source text (AI unavailable)')
  if (d.style) parts.push(`style ${String(d.style)}`)
  if (d.template) parts.push(`template ${String(d.template)}`)
  if (d.auto) parts.push('automatically')
  if (d.partialSource) parts.push('— only part of the article could be read')
  if (typeof d.scenes === 'number' && e.type === 'drafted') parts.push(`· ${d.scenes} scenes${d.voiceover ? ' from your voice-over' : ''}`)
  if (typeof d.seconds === 'number') parts.push(`· ${formatDuration(d.seconds)}`)
  if (d.location === 'agent' || e.type === 'waiting_for_agent') parts.push('on your computer')
  if (typeof d.credits === 'number' && d.credits > 0) parts.push(`· ${d.credits} video ${d.credits === 1 ? 'minute' : 'minutes'}`)
  if (d.error) parts.push(`— ${String(d.error)}`)
  if (e.actor === 'user' && !d.auto) parts.push('by you')
  return parts.join(' ')
}

export function when(ms: number): string {
  return new Date(ms).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
}
